import React, { useEffect, useMemo, useState } from "react";
import { format, getDaysInMonth, isSameDay, isSameMonth, isSameYear } from "date-fns";
import EquipmentService from "../../../services/Equipment.service";
import EquipmentCalendarGrid from "./EquipmentCalendarGrid";

export default function EquipmentCalendar({
    equipmentTypeId,
    equipmentList,
    refreshSignal,
    onStartCheck,
    onOpenDetails,
    onResolveIssue,
}) {
    const today = new Date();

    const [currentMonth, setCurrentMonth] = useState(today.getMonth());
    const [currentYear, setCurrentYear] = useState(today.getFullYear());
    const [inspections, setInspections] = useState([]);
    const [issues, setIssues] = useState([]);
    const [loading, setLoading] = useState(false);

    const dates = useMemo(() => {
        const count = getDaysInMonth(new Date(currentYear, currentMonth));
        return Array.from({ length: count }, (_, i) => new Date(currentYear, currentMonth, i + 1));
    }, [currentYear, currentMonth]);

    useEffect(() => {
        if (!equipmentTypeId) return;
        setLoading(true);
        Promise.all([
            EquipmentService.listInspections({
                equipment_type: equipmentTypeId,
                year: currentYear,
                month: currentMonth + 1,
            }),
            EquipmentService.listIssues({ equipment_type: equipmentTypeId, is_resolved: "false" }),
        ])
            .then(([inspRes, issueRes]) => {
                setInspections(Array.isArray(inspRes.data) ? inspRes.data : inspRes.data.results || []);
                setIssues(Array.isArray(issueRes.data) ? issueRes.data : issueRes.data.results || []);
            })
            .finally(() => setLoading(false));
    }, [equipmentTypeId, currentYear, currentMonth, refreshSignal]);

    const goBack = () => {
        if (currentMonth === 0) {
            setCurrentMonth(11);
            setCurrentYear((y) => y - 1);
        } else {
            setCurrentMonth((m) => m - 1);
        }
    };

    const goForward = () => {
        if (currentMonth === 11) {
            setCurrentMonth(0);
            setCurrentYear((y) => y + 1);
        } else {
            setCurrentMonth((m) => m + 1);
        }
    };

    const isCurrentMonth = isSameMonth(new Date(currentYear, currentMonth), today) && isSameYear(new Date(currentYear, currentMonth), today);
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const columns = useMemo(
        () =>
            dates.map((date) => ({
                key: date.toISOString(),
                label: format(date, "d"),
                isCurrent: isSameDay(date, today),
                isPast: date.getTime() < todayStart.getTime(),
                date,
            })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dates]
    );

    const inspectionFor = (equipmentId, columnKey) =>
        inspections.find(
            (i) =>
                i.equipment === equipmentId &&
                isSameDay(new Date(`${i.date}T00:00:00`), new Date(columnKey))
        );

    const openIssueFor = (equipmentId) => issues.find((i) => i.equipment === equipmentId) || null;

    const handleUncheck = async (inspection) => {
        setLoading(true);
        try {
            await EquipmentService.deleteInspection(inspection.id);
            setInspections((prev) => prev.filter((i) => i.id !== inspection.id));
        } finally {
            setLoading(false);
        }
    };

    return (
        <EquipmentCalendarGrid
            title={format(new Date(currentYear, currentMonth), "MMMM yyyy")}
            onPrev={goBack}
            onNext={goForward}
            nextDisabled={isCurrentMonth}
            columns={columns}
            equipmentList={equipmentList}
            loading={loading}
            inspectionFor={inspectionFor}
            openIssueFor={openIssueFor}
            onStartCheck={onStartCheck}
            onOpenDetails={onOpenDetails}
            onResolveIssue={onResolveIssue}
            onUncheck={handleUncheck}
        />
    );
}
